/**
 * agent.ts — LiteRT.js-backed inference agent for the active-perception demo.
 *
 * The trained GRU DRQN (see scratchbooks/_train_active_perception.py, validated
 * at 2.66× random on 20×20) ships as a .tflite FlatBuffer. This module:
 *   1. Loads the LiteRT.js wasm runtime.
 *   2. Loads and compiles the .tflite (WebGPU where available, WASM fallback).
 *   3. Runs pure inference per tick — NO training in the browser. The deadly
 *      triad lives in Python; the browser sees only a frozen policy.
 *
 * Per-tick contract (mirrors the prototype's GRUDRQN.forward_step):
 *
 *     x    = concat(prev_action_one_hot [8], obs [33])      // 41-d
 *     h_in = previous hidden state                          // 48-d, 0 on step 0
 *     [q, h_out] = model.run({ x, h_in })                   // q is 8-d
 *     a = argmax(q)            (with optional can_move masking)
 *     step the env, then h_in ← h_out for the next tick.
 *
 * Memory management: LiteRT.js uses MANUAL memory. Every Tensor we create or
 * receive must be `delete()`d. The per-tick hot path creates one input tensor
 * pair and consumes one output tensor pair; we delete both.
 */

import { loadLiteRt, loadAndCompile, Tensor, type CompiledModel } from '@litertjs/core';
import {
  Env, N_ACTIONS, D_OBS, MAX_STEPS,
} from './environment';
import { LoopGate } from './loop_gate';

const HIDDEN = 48;
const D_IN = N_ACTIONS + D_OBS; // 8 + 33 = 41

/** Module-level guard: the LiteRT.js wasm runtime can only be initialised
 *  ONCE per page -- calling loadLiteRt() twice throws "LiteRT is already
 *  loading / loaded." When the demo hosts two agents (baseline + memory
 *  variant), the second agent's load() must skip the runtime init and go
 *  straight to loadAndCompile(). This promise memoises the first call so
 *  concurrent first-time loads (Promise.all) share one init too. */
let litertRuntimePromise: Promise<void> | null = null;
function loadLiteRtOnce(wasmPath: string): Promise<void> {
  if (litertRuntimePromise) return litertRuntimePromise;
  litertRuntimePromise = (async () => {
    try {
      await loadLiteRt(wasmPath, { jspi: true });
    } catch (e) {
      console.warn('loadLiteRt with jspi failed; retrying without:', e);
      await loadLiteRt(wasmPath);
    }
  })();
  // If init fails, clear the memo so the next attempt can retry fresh.
  litertRuntimePromise.catch(() => { litertRuntimePromise = null; });
  return litertRuntimePromise;
}

/** One per-tick inference result for the HUD / renderer. */
export interface StepResult {
  action: number;
  reward: number;
  done: boolean;
  event: 'gold' | 'stone' | 'bump' | 'step';
  /** Q-values for all 8 candidate actions at this tick (for the side panel). */
  qValues: Float64Array;
  /** Hidden-state L2 norm — a sanity signal for "is the recurrence alive". */
  hNorm: number;
  /** Loop-gate diagnostics for the HUD. Null when the gate is disabled. */
  gate: LoopGateDiagnosticsForHud | null;
}

/** Slimmed-down view of LoopGateDiagnostics for the HUD — exposes only
 *  what the UI actually renders. */
export interface LoopGateDiagnosticsForHud {
  active: boolean;
  fireCount: number;
  masked: ReadonlySet<number>;
  speed: number;
  goldWindow: number;
  /** Cumulative actions blocked by the taboo mask this episode. */
  tabooBlocks: number;
}

export interface AgentOptions {
  /** Path to the .tflite model in the public/ dir. */
  modelUrl: string;
  /** Path to the LiteRT.js wasm directory (served statically). */
  wasmPath: string;
  /** Accelerator preference. 'webgpu' falls back to 'wasm' on failure. */
  accelerator?: 'webgpu' | 'wasm';
  /** If true, mask actions whose can_move feature is 0 (recommended). */
  actionMasking?: boolean;
  /** If true, enable the LoopGate — a diagnose-then-mask gate that
   *  detects oscillation basins and forces a recency tie-break to
   *  escape them. Default: true. Validated in Python at +4.2% mean
   *  gold collection (best +18.8%, worst -4.1% on the matched-pair
   *  eval; see scratchbooks/_proto_loop_gate.py). */
  loopGate?: boolean;
  /** If true, enable the basin-cell taboo mask (the second mask). This
   *  is a VARIANCE REDUCER, not an improvement: across 10 seeds it lifts
   *  the worst-case ratio from 0.94× to 0.99× at the cost of capping
   *  the best-case from 1.19× to 1.12×. Toggle on for smoother demos,
   *  off for peak performance. Default: off. */
  taboo?: boolean;
  /** LoopGate hyperparameters. If omitted, the validated defaults are
   *  used (see loop_gate.ts). To enable the taboo mask via options,
   *  pass `{ tabooDecay: 0.05, tabooRadius: 2 }` here. */
  loopGateOpts?: ConstructorParameters<typeof LoopGate>[0];
}

/**
 * Shared contract for inference agents. Both the baseline GRU
 * (LitertAgent) and the visited-memory variant (LitertMemAgent) implement
 * this, so main.ts / probe.ts can hold a single `Agent` reference and swap
 * it at runtime when the user picks a model from the sidebar.
 */
export interface Agent {
  /** LoopGate instance — public so probe.ts/main.ts can call .reset() etc. */
  readonly loopGate: LoopGate;
  /** Runtime toggles for the two masks. Mutable. */
  gateEnabled: boolean;
  tabooEnabled: boolean;
  load(): Promise<void>;
  startEpisode(env: Env): void;
  stepAsync(env: Env): Promise<StepResult>;
}

/**
 * The LiteRT.js-backed inference agent.
 *
 * Lifecycle:
 *   const agent = new LitertAgent(opts);
 *   await agent.load();        // fetch wasm + compile model (~100ms)
 *   agent.startEpisode(env);   // resets the recurrence (h ← 0, prev_a ← 0)
 *   while (!env.done) {
 *     const res = agent.step(env);
 *     ...
 *   }
 */
export class LitertAgent implements Agent {
  protected model: CompiledModel | null = null;
  protected h: Float32Array = new Float32Array(HIDDEN);
  protected prevAction: Float32Array = new Float32Array(N_ACTIONS);
  protected readonly opts: Required<Omit<AgentOptions, 'loopGateOpts'>>;
  /** The LoopGate. Always present; its effect is toggled by
   *  `gateEnabled`. Owned by the agent so callers don't have to thread
   *  it through every call — main.ts just reads `result.gate` for the HUD. */
  readonly loopGate: LoopGate;
  /** Mutable: set to false to disable the gate at runtime (the HUD
   *  toggle does this). The gate's internal state keeps updating
   *  regardless, so toggling back on mid-episode resumes smoothly. */
  gateEnabled: boolean;
  /** Mutable: set to true to enable the basin-cell taboo mask (the
   *  second mask) at runtime. Variance reducer — see AgentOptions.taboo.
   *  Off by default for peak smoke-test ratio; toggle on for smoother
   *  behavior on adversarial layouts. */
  tabooEnabled: boolean;
  /** Output-tensor indices, resolved at load-time by inspecting the model's
   *  signature (TFLite exports the output names as opaque 'StatefulPartitionedCall:N',
   *  so we identify the q and h_out tensors by their SHAPE in getOutputDetails()).
   *  qIdx is the output whose shape is [1, N_ACTIONS]; hOutIdx is [1, HIDDEN]. */
  protected qIdx = -1;
  protected hOutIdx = -1;
  /** Input-tensor slot order, resolved at load-time by shape. The LiteRT.js
   *  positional-input contract binds the array passed to model.run() to the
   *  input-details list IN ITS NATIVE ORDER (which is neither alphabetical
   *  by name nor the keras.Model declaration order -- TF SavedModel tracing
   *  emits whatever order it wants). We resolve the permutation once at
   *  load time so subclasses can build tensors in a stable, semantic order
   *  (h_in first, then any extras, then x) and have stepAsync permute them
   *  to match what the model actually expects.
   *
   *  inputSlotOrder[i] = the position in assembleInputs()'s returned array
   *  of the tensor that should go into input-details slot i. */
  protected inputSlotOrder: number[] = [];

  constructor(opts: AgentOptions) {
    this.opts = {
      modelUrl: opts.modelUrl,
      wasmPath: opts.wasmPath,
      accelerator: opts.accelerator ?? 'webgpu',
      actionMasking: opts.actionMasking ?? true,
      loopGate: opts.loopGate ?? true,
      taboo: opts.taboo ?? false,
    };
    this.gateEnabled = opts.loopGate ?? true;
    this.tabooEnabled = opts.taboo ?? false;
    // Configure the LoopGate with the taboo defaults (5×5 box, decay 0.05)
    // UNLESS the caller explicitly overrode them via loopGateOpts. This way
    // the runtime `tabooEnabled` toggle actually does something — without
    // this, the gate would be constructed with tabooDecay=0 and flipping
    // the toggle at runtime would have no effect.
    const gateOpts = opts.loopGateOpts ?? {};
    if (gateOpts.tabooDecay === undefined) gateOpts.tabooDecay = 0.05;
    if (gateOpts.tabooRadius === undefined) gateOpts.tabooRadius = 2;
    this.loopGate = new LoopGate(gateOpts);
  }

  /** Load the wasm runtime and compile the model. Resolves when the agent is
   *  ready to step. */
  async load(): Promise<void> {
    // Step 1: initialise the wasm runtime. Guarded by loadLiteRtOnce --
    // the runtime is a per-page singleton, so the second agent in a
    // two-agent demo (baseline + memory variant) skips this step.
    await loadLiteRtOnce(this.opts.wasmPath);

    // Step 2: load and compile the .tflite.
    let lastErr: unknown = null;
    for (const accel of this.opts.accelerator === 'webgpu'
      ? (['webgpu', 'wasm'] as const)
      : (['wasm'] as const)) {
      try {
        this.model = await loadAndCompile(this.opts.modelUrl, { accelerator: accel });
        console.log(`LiteRT.js: model loaded on '${accel}' backend`);
        // Resolve the output indices by inspecting the signature.
        const outDetails = this.model.getOutputDetails();
        for (const d of outDetails) {
          if (d.shape.length === 2 && d.shape[1] === N_ACTIONS) this.qIdx = d.index;
          else if (d.shape.length === 2 && d.shape[1] === HIDDEN) this.hOutIdx = d.index;
        }
        if (this.qIdx < 0 || this.hOutIdx < 0) {
          throw new Error(
            `could not identify q/h_out outputs by shape; got: ` +
            outDetails.map((d) => `${d.name}=${Array.from(d.shape)}`).join(', '));
        }
        // Resolve the INPUT slot order. LiteRT.js binds the positional
        // Tensor[] passed to model.run() to getInputDetails() IN ITS NATIVE
        // ORDER, which for TF-traced .tflites is neither alphabetical by
        // name nor the Keras declaration order (verified empirically: the
        // memory variant's inputs come out as [m_in, h_in, x] despite
        // being declared [x_in, h_in, m_in]). Match each native slot to
        // the canonical position returned by inputShapes() by SHAPE.
        // inputSlotOrder[nativeSlotIdx] = canonicalPositionIdx.
        const inDetails = this.model.getInputDetails();
        const canonical = this.inputShapes();
        this.inputSlotOrder = new Array(inDetails.length).fill(-1);
        for (let nativeIdx = 0; nativeIdx < inDetails.length; nativeIdx++) {
          const natShape = Array.from(inDetails[nativeIdx].shape);
          let match = -1;
          for (let canonIdx = 0; canonIdx < canonical.length; canonIdx++) {
            // Only claim an unused canonical slot (so two same-shaped inputs
            // don't both grab the first match).
            if (this.inputSlotOrder.indexOf(canonIdx) !== -1) continue;
            const cShape = canonical[canonIdx];
            if (cShape.length === natShape.length
                && cShape.every((v, i) => v === natShape[i])) {
              match = canonIdx;
              break;
            }
          }
          if (match < 0) {
            throw new Error(
              `could not match input slot ${nativeIdx} ` +
              `(shape=${natShape.join(',')} name=${inDetails[nativeIdx].name}) ` +
              `to any canonical input shape: ` +
              canonical.map((s) => `[${s.join(',')}]`).join(', '));
          }
          this.inputSlotOrder[nativeIdx] = match;
        }
        console.log(`LiteRT.js: input slot order (native→canonical): ` +
                    `[${this.inputSlotOrder.join(', ')}]`);
        console.log(`LiteRT.js: output indices  q=${this.qIdx}  h_out=${this.hOutIdx}`);
        return;
      } catch (e) {
        console.warn(`loadAndCompile('${accel}') failed:`, e);
        lastErr = e;
      }
    }
    throw new Error(`LiteRT.js failed to load model on any backend: ${lastErr}`);
  }

  /** Reset the recurrence at the start of an episode. */
  startEpisode(_env: Env): void {
    this.h.fill(0);
    this.prevAction.fill(0);
    // Reset the loop-gate's per-episode state. Seed the recency map
    // with the start cell so the tie-breaker has a sensible baseline.
    this.loopGate.reset();
    this.loopGate.seedRecency(_env.r, _env.c);
  }

  /** Build the per-tick input tensor list IN A CANONICAL ORDER defined by
   *  the variant. The baseline model has two inputs ('h_in', 'x') → order
   *  [h_in, x]. Subclasses with extra inputs (e.g. 'm_in' for the visited
   *  bitmap) override this AND inputShapes() to insert the extra tensor in
   *  the canonical order: [h_in, m_in, x].
   *
   *  IMPORTANT: the order returned here is NOT necessarily the order the
   *  model.run() call expects -- TFLite flattens inputs in its own native
   *  input-details order, which is neither alphabetical by name nor the
   *  Keras declaration order. stepAsync() permutes this array into model
   *  order at runtime, using a permutation resolved at load time by
   *  matching inputShapes() against getInputDetails(). So: keep this order
   *  stable and semantic; the load-time resolver handles the rest.
   *
   *  Caller owns the returned tensors and must `.delete()` them after the
   *  model.run() promise resolves.
   *
   *  Env is passed so subclasses can read whatever extra state they need
   *  (the memory variant reads env.shadow.confident). The baseline ignores it.
   *
   *  NOTE: `xArr` is already filled with [prev_action_one_hot, obs] by the
   *  caller (stepAsync). Override implementations may extend xArr's
   *  contents (they don't here) but the contract is: take xArr as-is and
   *  wrap it in a Tensor. */
  protected assembleInputs(_env: Env, _obs: Float64Array, xArr: Float32Array): Tensor[] {
    const xTensor = new Tensor(xArr, [1, D_IN]);
    const hInTensor = new Tensor(new Float32Array(this.h), [1, HIDDEN]);
    return [hInTensor, xTensor];
  }

  /** Shape of each tensor returned by assembleInputs(), in the SAME order.
   *  Used at load time to match each canonical slot against the model's
   *  input-details list (which can be in any order). Override in subclasses
   *  that add extra inputs. */
  protected inputShapes(): number[][] {
    return [[1, HIDDEN], [1, D_IN]];
  }

  /** Async step. LiteRT.js inference returns Promises (the WebGPU path queues
   *  GPU work; reading output data requires an async `.moveTo('wasm')`). The
   *  tick loop must `await agent.stepAsync(env)`. */
  async stepAsync(env: Env): Promise<StepResult> {
    if (!this.model) throw new Error('agent.stepAsync called before agent.load');

    const obs = env.obs();
    const xArr = new Float32Array(D_IN);
    xArr.set(this.prevAction, 0);
    xArr.set(obs, N_ACTIONS);

    // Variant-specific input assembly. Returns the input Tensor[] in a
    // stable CANONICAL order (h_in, then any extras, then x). The cleanup
    // loop below iterates this same array, so any extra tensors added by a
    // subclass are freed automatically.
    const canonicalInputs = this.assembleInputs(env, obs, xArr);

    // Permute into the model's NATIVE input-details order. TFLite flattens
    // inputs in whatever order TF tracing emitted (neither alphabetical by
    // name nor the Keras declaration order); the load-time resolver matched
    // each native slot to a canonical position by shape.
    const inputs: Tensor[] = new Array(canonicalInputs.length);
    for (let nativeIdx = 0; nativeIdx < this.inputSlotOrder.length; nativeIdx++) {
      inputs[nativeIdx] = canonicalInputs[this.inputSlotOrder[nativeIdx]];
    }

    // Call the model with POSITIONAL inputs (Tensor[]) — the named-input
    // overload keys output tensor names to the model's signature, which
    // TFLite exports as opaque names like 'StatefulPartitionedCall:0'. With
    // positional inputs we get a positional output array we index by the
    // resolved qIdx / hOutIdx (captured at load-time via getOutputDetails()).
    //
    // INPUT ORDER: `inputs` is now in native input-details order (resolved
    // at load-time by shape matching). Baseline: typically [h_in, x] (but
    // we don't assume it). Memory variant: typically [m_in, h_in, x].
    const outputs = await this.model.run(inputs);
    // outputs is a Tensor[] in the model's output-index order. We resolved
    // qIdx and hOutIdx at load-time.
    // The output array is in the order from getOutputDetails(); since the
    // indices match positions in that array, we can index directly.
    // Build an index → position map (output array order isn't guaranteed to
    // match the index numbering, though in practice it does).
    const qTensor = outputs[this.qIdx] ?? outputs[0];
    const hOutTensor = outputs[this.hOutIdx] ?? outputs[1];
    if (!qTensor || !hOutTensor) {
      inputs.forEach((t) => t.delete());
      outputs.forEach((t) => t.delete());
      throw new Error('LiteRT.js: output tensors missing at resolved indices');
    }

    // Move outputs to the wasm buffer so we can read them as a TypedArray.
    // (On the wasm backend this is a no-op; on webgpu it's a GPU→CPU copy.)
    const qInWasm = await qTensor.moveTo('wasm');
    const hOutInWasm = await hOutTensor.moveTo('wasm');
    const qArr = qInWasm.toTypedArray() as Float32Array;
    const hOutArr = hOutInWasm.toTypedArray() as Float32Array;

    // ── Action selection ─────────────────────────────────────────────
    // Three masks compose, in this order:
    //   1. can_move     — the existing hard mask (cells known to be stone)
    //   2. loop_mask    — actions banned by the LoopGate this tick
    //   3. tie-break    — if the gate is active, pick the least-recently-
    //                     used destination cell instead of argmax-of-rest.
    // (See loop_gate.ts for why argmax-of-rest doesn't work and recency
    // does. The recipe mirrors mixture-of-bandits.md's diagnose-then-mask.)

    // Step 1: can_move mask.
    const canMove = new Uint8Array(N_ACTIONS);
    for (let a = 0; a < N_ACTIONS; a++) {
      canMove[a] = (this.opts.actionMasking && obs[a * 4 + 2] < 0.5) ? 0 : 1;
    }

    // Step 2: query the gate. If the gate is disabled, preStep returns
    // (empty mask, false) and the agent behaves exactly as before.
    const gateEnabled = this.gateEnabled;
    const [loopMask, useTiebreak] = gateEnabled
      ? this.loopGate.preStep()
      : [new Set<number>(), false];

    // Compose: valid = can_move AND NOT loop_mask.
    const valid = new Uint8Array(N_ACTIONS);
    let anyValid = false;
    for (let a = 0; a < N_ACTIONS; a++) {
      const v = canMove[a] === 1 && !loopMask.has(a) ? 1 : 0;
      valid[a] = v;
      if (v === 1) anyValid = true;
    }

    // Second mask: basin-cell taboo. Bans actions whose destination is
    // in a recently-escaped basin's neighborhood (5×5 box, decaying).
    // Variance reducer — see AgentOptions.taboo. Only fires when both
    // the gate and the taboo toggle are on; if it removes everything
    // legal, we peel back to the pre-taboo valid set (safety).
    if (gateEnabled && this.tabooEnabled) {
      const tabooBanned = this.loopGate.tabooMaskForPose(env.r, env.c, valid);
      if (tabooBanned.size > 0) {
        let anyAfterTaboo = false;
        for (const a of tabooBanned) { valid[a] = 0; }
        for (let a = 0; a < N_ACTIONS; a++) if (valid[a] === 1) { anyAfterTaboo = true; break; }
        if (!anyAfterTaboo) {
          // Peel back: re-apply can_move + loop_mask without taboo.
          for (let a = 0; a < N_ACTIONS; a++) {
            valid[a] = canMove[a] === 1 && !loopMask.has(a) ? 1 : 0;
          }
        }
      }
    }

    if (!anyValid) {
      // Safety: the loop mask removed everything legal. Fall back to
      // can_move alone — the agent must always have ≥1 legal move.
      for (let a = 0; a < N_ACTIONS; a++) valid[a] = canMove[a];
    }

    let bestA = 0;
    if (useTiebreak && gateEnabled) {
      // Recency tie-break: among valid actions, pick the one whose
      // destination cell was visited least recently.
      const lru = this.loopGate.pickLeastRecentlyUsed(valid, env.r, env.c);
      bestA = lru >= 0 ? lru : argmaxOver(valid, qArr);
    } else {
      bestA = argmaxOver(valid, qArr);
    }

    // Step the env.
    const out = env.step(bestA);

    // Step 3: tell the gate about the outcome so it can update its
    // detector + persistent mask state.
    if (gateEnabled) {
      this.loopGate.postStep(env.r, env.c, out.reward, bestA);
    }

    // Thread the recurrence forward.
    this.h.set(hOutArr);
    this.prevAction.fill(0);
    this.prevAction[bestA] = 1.0;

    // Compute hidden-state norm for the HUD.
    let hNorm = 0;
    for (let i = 0; i < HIDDEN; i++) hNorm += this.h[i] * this.h[i];
    hNorm = Math.sqrt(hNorm);

    // Clean up tensors — manual memory management. Iterate the input array
    // (whatever its length) so subclass-added tensors are freed too.
    for (const t of inputs) t.delete();
    qTensor.delete();
    hOutTensor.delete();
    qInWasm.delete();
    hOutInWasm.delete();

    // Snapshot gate diagnostics for the HUD.
    let gateDiag: LoopGateDiagnosticsForHud | null = null;
    if (gateEnabled) {
      const d = this.loopGate.diagnostics();
      gateDiag = {
        active: d.gateActive,
        fireCount: d.fireCount,
        masked: d.masked,
        speed: d.speed,
        goldWindow: d.goldWindow,
        tabooBlocks: d.tabooBlocks,
      };
    }

    return {
      action: bestA,
      reward: out.reward,
      done: out.done,
      event: out.event,
      qValues: Float64Array.from(qArr),
      hNorm,
      gate: gateDiag,
    };
  }
}

/** argmax over q[a] restricted to where valid[a] === 1. */
function argmaxOver(valid: Uint8Array, q: ArrayLike<number>): number {
  let best = -Infinity;
  let bestA = 0;
  for (let a = 0; a < N_ACTIONS; a++) {
    if (valid[a] !== 1) continue;
    if (q[a] > best) { best = q[a]; bestA = a; }
  }
  return bestA;
}

// ─── Random rollout (for the head-to-head comparator) ────────────────────────

/** Run one episode with a uniformly-random policy. Returns total gold value
 *  collected. The env's RNG drives the action sampling so different episodes
 *  differ. */
export function runRandomEpisode(env: Env): number {
  env.reset();
  while (!env.done) {
    const a = Math.floor(env.rng() * N_ACTIONS);
    env.step(a);
  }
  return env.totalCollected;
}

/** Run one episode with the trained agent (greedy). Takes the shared Agent
 *  interface so either variant (baseline or memory) can be passed in. */
export async function runGreedyEpisode(agent: Agent, env: Env): Promise<number> {
  env.reset();
  agent.startEpisode(env);
  let safety = 0;
  while (!env.done && safety++ < MAX_STEPS * 2) {
    await agent.stepAsync(env);
  }
  return env.totalCollected;
}
